import { GuardPolicyDtoStatus, Jurisdiction, Location } from '../../../../api/models'
import { AgencyTypeValues } from '../../../../constants/AgencyType'
import { PermitRenewalCadence, PermitRenewalCadenceDisplayNames } from '../../../../constants/PermitRenewalCadence'
import { formatIsoTimestampForLocation } from '../../../../utils/dateUtils'

export function getRenewalRequirement(jurisdiction: Jurisdiction, agencyType: AgencyTypeValues): string {
  if (jurisdiction.agencyRules[agencyType]?.permitRenewal?.isRequired) {
    return 'Required'
  } else {
    return 'Not Required'
  }
}

export function getRenewalCadence(jurisdiction: Jurisdiction, agencyType: AgencyTypeValues): string {
  if (jurisdiction.agencyRules[agencyType]?.permitRenewal?.cadence) {
    return PermitRenewalCadenceDisplayNames[jurisdiction.agencyRules[agencyType]?.permitRenewal?.cadence]
  }
  return PermitRenewalCadenceDisplayNames[PermitRenewalCadence.Unknown]
}

export function getRenewalResponsible(jurisdiction: Jurisdiction): string {
  const isSimpliSafeManaged = jurisdiction.policies.renewal.isSimpliSafeManaged
  if (isSimpliSafeManaged) {
    return 'SimpliSafe Renewal'
  } else {
    return 'Customer Renewal'
  }
}

export function getPermitResponsible(jurisdiction: Jurisdiction): string {
  const isSimpliSafeManaged = jurisdiction.policies.registration.isSimpliSafeManaged
  if (isSimpliSafeManaged) {
    return 'SimpliSafe Registration'
  } else {
    return 'Customer Registration'
  }
}

export function getLastResearchedDate(jurisdiction: Jurisdiction, agencyType: AgencyTypeValues, location: Location | undefined): string {
  if (!jurisdiction.agencyRules[agencyType]?.lastResearchedDate) {
    return 'Not available'
  }
  const date = jurisdiction.agencyRules[agencyType]?.lastResearchedDate
  return formatIsoTimestampForLocation(date, location)
}

export function getGuardPolicy(jurisdiction: Jurisdiction): string {
  const guardPolicy = jurisdiction.policies.guard?.status
  if (!guardPolicy) {
    return 'Not available'
  }
  switch (guardPolicy) {
    case GuardPolicyDtoStatus.GuardAutoEnrolled:
      return 'Guard Service Auto-Enrolled'
    case GuardPolicyDtoStatus.GuardRecommended:
      return 'Guard Service Recommended'
    case GuardPolicyDtoStatus.None:
      return 'Guard Service Not Necessary'
  }
}

export function isPrivateResponseRequired(jurisdiction: Jurisdiction, agencyType: AgencyTypeValues): string {
  const isRequired = jurisdiction.agencyRules[agencyType]?.isPrivateResponseRequired
  if (isRequired) {
    return 'Required'
  } else if (!isRequired) {
    return 'Not required'
  } else {
    return 'Unknown'
  }
}

export function getCallVerification(jurisdiction: Jurisdiction, agencyType: AgencyTypeValues): string {
  const enhancedCallVerificationRequirements = jurisdiction.agencyRules[agencyType]?.enhancedCallVerificationRequirements
  if (!enhancedCallVerificationRequirements?.raw.code) {
    return 'Not available'
  }
  const callsRequired = enhancedCallVerificationRequirements?.count
  if (callsRequired > 0) {
    return `${callsRequired} calls required`
  } else {
    return 'No minimum required'
  }
}

export function getNumberOfFalseAlarmsBeforeSuspension(jurisdiction: Jurisdiction, agencyType: AgencyTypeValues): string {
  const falseAlarmRules = jurisdiction.agencyRules[agencyType]?.falseAlarmRules
  if (
    (!falseAlarmRules?.suspendable?.raw || !falseAlarmRules?.suspendable?.raw?.limit) &&
    jurisdiction.agencyRules[agencyType]?.permitAdditionalNotes
  ) {
    return 'See Comment'
  } else if (!falseAlarmRules?.suspendable?.raw || !falseAlarmRules?.suspendable?.raw?.limit) {
    return 'Not available'
  }
  const limit = Number(falseAlarmRules?.suspendable?.raw?.limit)
  if (limit > 0) {
    return limit.toString()
  } else {
    return 'See Comment'
  }
}

export function getNumberOfFalseAlarmsBeforeFine(jurisdiction: Jurisdiction, agencyType: AgencyTypeValues): string {
  const falseAlarmRules = jurisdiction.agencyRules[agencyType]?.falseAlarmRules
  if ((!falseAlarmRules?.fineable?.raw || !falseAlarmRules?.fineable?.raw?.limit) && jurisdiction.agencyRules[agencyType]?.permitAdditionalNotes) {
    return 'See Comment'
  } else if (!falseAlarmRules?.fineable?.raw || !falseAlarmRules?.fineable?.raw?.limit) {
    return 'Not available'
  }
  const limit = Number(falseAlarmRules?.fineable?.raw?.limit)
  if (limit > 0) {
    return limit.toString()
  } else {
    return 'See Comment'
  }
}

export const reformatValuesToYesOrNo = (originalValue?: string): string => {
  if (originalValue === undefined) return ''
  return originalValue === 'N' ? 'No' : 'Yes'
}
